import {queryOptions, type UseQueryOptions} from '@tanstack/react-query';
import {isNil} from 'lodash';
import {AUTH_QUERY_KEY_MAP} from '../api/auth/constant';
import {getAuthGetagreementJoin14} from '../api/auth/getAuthGetagreementJoin14';
import {getAuthGetagreementPersonalInfo} from '../api/auth/getAuthGetagreementPersonalInfo';
import {getAuthParentVerifyIsCompleted} from '../api/auth/getAuthParentVerifyIsCompleted';
import {getAuthUnregisterCheck} from '../api/auth/getAuthUnregisterCheck';
import {postAuthAppActivateUser} from '../api/auth/postAuthAppActivateUser';
import {postAuthGetagreement} from '../api/auth/postAuthGetagreement';
import {postAuthReqInfo} from '../api/auth/postAuthReqInfo';
import {postGatewayAuthProviderAgreement} from '../api/auth/postGatewayAuthProviderAgreement';
import {postGatewayUserGetAgeAuth} from '../api/user/postGatewayUserGetAgeAuth';
import type {FetcherOptions} from '../axios/axiosBffFetch';
import {t} from '../common/getTranslations';
import {requestContext} from '../common/requestContext';
import type {LegalAgentAgreementVo} from '../common/shared/api/nicolas/vo/LegalAgentAgreementVo.interface';
import type {PersonalInfoAgreementVo} from '../common/shared/api/nicolas/vo/PersonalInfoAgreementVo.interface';
import {getIsLegacyVersionUpdateError} from '../dialog/legacyVersionUpdateDialog';
import {getSerializedPagewebError} from '../errors/getSerializedPagewebError.function';
import {PagewebError} from '../errors/PagewebError.class';
import {PagewebErrorType} from '../errors/PagewebErrorType.enum';
import {CustomApolloError} from '../graphql/api/CustomApolloError';
import {createSimpleApiResponseError} from '../graphql/api/createSimpleApiResponseError';
import {createActivateUser} from '../graphql/factory/ActivateUser';
import {createAgreementInfo} from '../graphql/factory/AgreementInfo';
import {createAuthRequestInfo} from '../graphql/factory/AuthRequestInfo';
import {createCheckUnregister} from '../graphql/factory/CheckUnregister';
import {createGlobalAgreementInfo} from '../graphql/factory/GlobalAgreementInfo';
import {createGlobalParentVerificationIsCompleted} from '../graphql/factory/GlobalParentVerificationIsCompleted';
import {createLegalAgentAgreement} from '../graphql/factory/legalAgentAgreement';
import {createPersonalInfoAgreement} from '../graphql/factory/PersonalInfoAgreement';
import type {
  ActivateUserQuery,
  ActivateUserQueryVariables,
  AgreementsQuery,
  AgreementsQueryVariables,
  CheckUnregisterQuery,
  CheckUnregisterQueryVariables,
  GetGlobalAgreementsQuery,
  GetGlobalAgreementsQueryVariables,
  GetGlobalParentVerificationIsCompletedQuery,
  GetGlobalParentVerificationIsCompletedQueryVariables,
  PrepareAuthRequestInfoQuery,
  PrepareAuthRequestInfoQueryVariables,
  SelfCertAgreementQuery,
  SelfCertAgreementQueryVariables,
} from '../graphql/generated/types';
import {
  AuthRequestType as AuthRequestTypeEnum,
  CertType,
} from '../graphql/generated/types';
import {
  PARENT_SELF_CERT_FAIL_ERROR_MESSAGE,
  SELF_CERT_FAIL_ERROR_MESSAGE,
} from '../selfCert/constants';

/**
 * Auth 도메인 Query Factory
 *
 * Adapter layer를 제거하고 Factory에서 직접 데이터 변환을 수행
 */
export const authFactory = {
  /**
   * @TODO: 휴면계정을 다시 활성화 하는 기능인데 현재는 사용하지 않음 ❌
   * @DEPRECATED
   * 사용자 활성화
   *
   * ⚠️ Resolver 로직:
   * - authInput을 가공하여 accessToken, appUserId 추출
   * - createActivateUser factory로 응답 변환
   */
  activateUser: (
    variables?: ActivateUserQueryVariables,
    options?: Omit<
      UseQueryOptions<ActivateUserQuery, PagewebError, ActivateUserQuery>,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    queryOptions({
      queryKey: AUTH_QUERY_KEY_MAP.activateUser(variables),
      queryFn: async () => {
        const authInput = variables?.authInput;
        const kakaoAuth = requestContext.requestInfo.kakaoAuth;

        const accessToken = authInput?.accessToken ?? kakaoAuth?.accessToken;
        const appUserId =
          authInput?.appUserId ??
          (!isNil(kakaoAuth?.appUserId)
            ? Number(kakaoAuth.appUserId)
            : undefined);

        if (!accessToken || isNil(appUserId)) {
          throw new CustomApolloError({
            isSerializedPagewebError: true,
            type: PagewebErrorType.KakaoAuthError,
            message: t('alert_message_for_not_logged_in_user'),
          });
        }

        const authParams = {
          accessToken,
          appUserId,
        };

        const response = await postAuthAppActivateUser(
          authParams,
          fetcherOptions
        );

        return {
          activateUser: createActivateUser(response),
        };
      },
      ...options,
    }),

  /**
   * 약관 정보 조회
   *
   * ⚠️ 서버 사이드 로직 제외됨:
   * - 원본 Resolver에는 kakaoAuth 필수 검증 존재
   * - requestContext.requestInfo.display.isWebView 사용 (서버 사이드 컨텍스트)
   * - 클라이언트 환경에서는 authParams와 isWebView를 직접 전달해야 함
   * - kakaoAuth가 없으면 에러가 발생하는 로직은 컴포넌트에서 처리 필요
   */
  agreements: (
    variables?: AgreementsQueryVariables,
    options?: Omit<
      UseQueryOptions<AgreementsQuery, PagewebError, AgreementsQuery>,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    queryOptions({
      queryKey: AUTH_QUERY_KEY_MAP.agreements(variables),
      queryFn: async () => {
        const kakaoAuth = requestContext.requestInfo.kakaoAuth;
        const isWebView = requestContext.requestInfo.display.isWebView;

        if (!kakaoAuth) {
          throw new CustomApolloError({
            isSerializedPagewebError: true,
            type: PagewebErrorType.KakaoAuthError,
            message: t('alert_message_for_not_logged_in_user'),
          });
        }

        const authParams = {
          accessToken: kakaoAuth.accessToken,
          appUserId: Number(kakaoAuth.appUserId),
        };

        const response = await postAuthGetagreement(authParams, fetcherOptions);

        return {
          agreements: createAgreementInfo(response, isWebView ?? false),
        };
      },
      ...options,
    }),

  /**
   * 회원 탈퇴 가능 여부 확인
   *
   * ⚠️ Resolver 로직:
   * - Factory 함수로 응답 변환 (snake_case → camelCase)
   */
  checkUnregister: (
    variables?: CheckUnregisterQueryVariables,
    options?: Omit<
      UseQueryOptions<CheckUnregisterQuery, PagewebError, CheckUnregisterQuery>,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    queryOptions({
      queryKey: AUTH_QUERY_KEY_MAP.checkUnregister(variables),
      queryFn: async () => {
        const response = await getAuthUnregisterCheck(fetcherOptions);
        const data = response.result;

        return {
          checkUnregister: createCheckUnregister(data),
        };
      },
      ...options,
    }),

  /**
   * 글로벌 약관 정보 조회
   *
   * ⚠️ Resolver 로직:
   * - provider를 URL path에 동적으로 삽입
   * - trimUndefinedFromObject 사용
   * - response.result 반환
   * - createGlobalAgreementInfo factory로 변환 (isWebView, isGlobal=true 전달)
   */
  getGlobalAgreements: (
    variables: GetGlobalAgreementsQueryVariables,
    options?: Omit<
      UseQueryOptions<
        GetGlobalAgreementsQuery,
        PagewebError,
        GetGlobalAgreementsQuery
      >,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    queryOptions({
      queryKey: AUTH_QUERY_KEY_MAP.getGlobalAgreements(variables),
      queryFn: async () => {
        const response = await postGatewayAuthProviderAgreement(
          variables.globalAgreementInput,
          fetcherOptions
        );

        const isWebView = requestContext.requestInfo.display.isWebView;

        return {
          getGlobalAgreements: createGlobalAgreementInfo(
            response.result,
            isWebView,
            true
          ),
        };
      },
      ...options,
    }),

  /**
   * 글로벌 부모 인증 완료 여부 조회
   *
   * ⚠️ Resolver 로직:
   * - input을 verifyUuid로 변환
   * - response.is_completed를 isCompleted로 변환
   */
  getGlobalParentVerificationIsCompleted: (
    variables: GetGlobalParentVerificationIsCompletedQueryVariables,
    options?: Omit<
      UseQueryOptions<
        GetGlobalParentVerificationIsCompletedQuery,
        PagewebError,
        GetGlobalParentVerificationIsCompletedQuery
      >,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    queryOptions({
      queryKey:
        AUTH_QUERY_KEY_MAP.getGlobalParentVerificationIsCompleted(variables),
      queryFn: async () => {
        const response = await getAuthParentVerifyIsCompleted(
          variables.input,
          fetcherOptions
        );

        return {
          getGlobalParentVerificationIsCompleted:
            createGlobalParentVerificationIsCompleted(response),
        };
      },
      ...options,
    }),

  /**
   * 본인 인증 약관 조회
   *
   * ⚠️ Resolver 로직 (부분 복제됨):
   * 1) authRequestType에 따라 조건부 API 호출
   *    - Parents: createLegalAgentAgreement
   *    - Self: createPersonalInfoAgreement
   * 2) ⚠️ 제외된 로직: kakaoAuth 필수 검증 (res.locals.jwt)
   *    - 클라이언트에서는 authParams를 직접 전달해야 함
   */
  selfCertAgreement: (
    variables: SelfCertAgreementQueryVariables,
    options?: Omit<
      UseQueryOptions<
        SelfCertAgreementQuery,
        PagewebError,
        SelfCertAgreementQuery
      >,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    queryOptions({
      queryKey: AUTH_QUERY_KEY_MAP.selfCertAgreement(variables),
      queryFn: async () => {
        const {authRequestType} = variables;
        const isWebView = requestContext.requestInfo.display.isWebView;
        const kakaoAuth = requestContext.requestInfo.kakaoAuth;

        if (!kakaoAuth) {
          throw new Error('인증 정보가 없습니다.');
        }

        const authParams = {
          accessToken: kakaoAuth.accessToken,
          appUserId: Number(kakaoAuth.appUserId),
        };

        if (authRequestType === AuthRequestTypeEnum.Parents) {
          const response = await getAuthGetagreementJoin14(
            authParams,
            fetcherOptions
          );

          return {
            selfCertAgreement: createLegalAgentAgreement(
              response as LegalAgentAgreementVo,
              isWebView
            ),
          };
        }

        const response = await getAuthGetagreementPersonalInfo(
          authParams,
          fetcherOptions
        );

        return {
          selfCertAgreement: createPersonalInfoAgreement(
            response as PersonalInfoAgreementVo,
            isWebView
          ),
        };
      },
      ...options,
    }),

  /**
   * 본인인증 요청 정보 준비
   *
   * ⚠️ Resolver 로직 (완전 복제됨):
   * - bypassSelfAuth가 true면 mock 데이터 반환
   * - certType이 DreamSecurity면 빈 응답 반환 (authRequestInfoErrors: [])
   * - Nice: auth_url 필수 검증, 없으면 에러 throw
   * - authRequestType에 따라 다른 에러 메시지 (Parents vs Self)
   */
  prepareAuthRequestInfo: (
    variables: PrepareAuthRequestInfoQueryVariables,
    options?: Omit<
      UseQueryOptions<
        PrepareAuthRequestInfoQuery,
        PagewebError,
        PrepareAuthRequestInfoQuery
      >,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    queryOptions({
      queryKey: AUTH_QUERY_KEY_MAP.prepareAuthRequestInfo(variables),
      queryFn: async () => {
        const {input} = variables;

        // 1. bypassSelfAuth 체크 → mock 데이터 반환
        if (requestContext.requestInfo.bypassSelfAuth) {
          const mockData = {
            auth_url: 'bypassSelfAuth',
            result_code: 0,
            result_message: 'SUCCESS',
          };

          return {
            prepareAuthRequestInfo: {
              authRequestInfo: createAuthRequestInfo(mockData as never),
              authRequestInfoErrors: [],
            },
          };
        }

        // 2. DreamSecurity → 빈 응답 (SDK 통해 바로 호출)
        if (input.certType === CertType.DreamSecurity) {
          /**
           * DreamSecurity 본인인증 자체는 SDK가 처리해 BFF /req/info 응답을 클라에서 받을 수 없다.
           * 구버전 앱(통합계정) 진입 시 업데이트 팝업을 띄우기 위해 사이드이펙트 없는
           * /api/v2/user/get/age_auth로 result_code=-99 여부만 사전 체크한다.
           * -99면 axiosBffFetch가 PagewebError throw → throwOnError:true로
           * useQuery 위로 전파 → SelfCertErrorBoundary가 다이얼로그 노출.
           * 사실상 -99 확인하려는 ping 용도의 더미 api. (월터 확인)
           * https://kakaoent.atlassian.net/browse/KA-11228?focusedCommentId=507158
           */
          try {
            await postGatewayUserGetAgeAuth(1);
          } catch (error) {
            const serializedError = getSerializedPagewebError(error);
            if (getIsLegacyVersionUpdateError(serializedError?.resultCode)) {
              throw error;
            }
          }
          return {
            prepareAuthRequestInfo: {
              authRequestInfo: null,
              authRequestInfoErrors: [],
            },
          };
        }

        // 3. API 호출
        const response = await postAuthReqInfo({
          certType: input.certType,
          authRequestType: input.authRequestType,
          returnUrl: input.returnUrl,
        });

        // 4. Nice 검증
        if (input.certType === CertType.Nice && !response.auth_url) {
          throw createSimpleApiResponseError(
            input.authRequestType === AuthRequestTypeEnum.Parents
              ? PARENT_SELF_CERT_FAIL_ERROR_MESSAGE
              : SELF_CERT_FAIL_ERROR_MESSAGE
          );
        }

        return {
          prepareAuthRequestInfo: {
            authRequestInfo: createAuthRequestInfo(response),
            authRequestInfoErrors: [],
          },
        };
      },
      ...options,
    }),
} as const;
